/*
 * drag-scroll.js
 * Lets any horizontally scrolling row be dragged with a mouse on desktop,
 * including when the drag starts on an image or a link inside the row.
 *
 * Why the image guard exists: every tile is an <a> wrapping an <img>. Pressing
 * and moving on one makes the browser start its own native "drag this image /
 * link" action, which cancels the pointer stream (pointercancel) and kills the
 * scroll. So drags only worked on the empty gaps between tiles. We switch that
 * native behaviour off inside scroll rows, which lets the pointer events reach
 * the scroll handlers. A plain click still opens the link; a click that follows
 * a drag is swallowed so dragging never navigates.
 *
 * Touch devices scroll natively, so pointerType 'touch' is ignored throughout.
 * Rows that ship their own drag handler (.scroll-showcase__row, .sbc__row) are
 * not bound twice; they only receive the native-drag guard below.
 */
(function () {
  'use strict';

  var OWN_HANDLER = '.scroll-showcase__row, .sbc__row';
  var ALL_ROWS = '.scroll-showcase__row, .sbc__row, [data-drag-scroll="on"]';
  var NEVER = 'header, .menu-drawer, .cart-drawer, .drawer, .modal, dialog, .shopify-section-group-header-group';
  var THRESHOLD = 5;

  /* ---- 1. Guard: stop native image / link dragging inside scroll rows ---- */

  (function injectStyle() {
    if (document.getElementById('drag-scroll-style')) return;
    var css =
      '.scroll-showcase__row, .sbc__row, [data-drag-scroll="on"] { cursor: grab; -webkit-user-select: none; user-select: none; }' +
      '.scroll-showcase__row.is-dragging, .sbc__row.is-dragging, [data-drag-scroll="on"].is-dragging { cursor: grabbing; }' +
      '.scroll-showcase__row img, .sbc__row img, [data-drag-scroll="on"] img,' +
      '.scroll-showcase__row a, .sbc__row a, [data-drag-scroll="on"] a { -webkit-user-drag: none; user-drag: none; }';
    var style = document.createElement('style');
    style.id = 'drag-scroll-style';
    style.textContent = css;
    (document.head || document.documentElement).appendChild(style);
  })();

  // Capture phase so it runs before anything else can start a native drag.
  document.addEventListener('dragstart', function (e) {
    var t = e.target;
    if (t && t.closest && t.closest(ALL_ROWS)) e.preventDefault();
  }, true);

  // Firefox honours the draggable attribute rather than the CSS above.
  function disableNativeDrag(root) {
    var els = root.querySelectorAll('img, a');
    for (var i = 0; i < els.length; i++) {
      if (els[i].getAttribute('draggable') !== 'false') els[i].setAttribute('draggable', 'false');
    }
  }

  /* ---- 2. Drag-to-scroll for rows that do not have their own handler ---- */

  function scrollableX(el) {
    if (el.scrollWidth - el.clientWidth <= 4) return false;
    if (el.clientWidth < 200) return false;
    var ox = window.getComputedStyle(el).overflowX;
    return ox === 'auto' || ox === 'scroll';
  }

  function skip(el) {
    if (el.closest(OWN_HANDLER)) return true;
    if (el.closest(NEVER)) return true;
    return false;
  }

  function bind(el) {
    if (el.dataset.dragScroll === 'on') return;
    el.dataset.dragScroll = 'on';

    var down = false;
    var moved = false;
    var startX = 0;
    var startLeft = 0;
    var prevSnap = '';
    var prevBehavior = '';
    var pointerId = null;

    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;
      if (e.button !== 0) return;
      if (e.target.closest && e.target.closest('input, textarea, select, option, button, [contenteditable="true"]')) return;
      down = true;
      moved = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startLeft = el.scrollLeft;
      prevSnap = el.style.scrollSnapType;
      prevBehavior = el.style.scrollBehavior;
    });

    el.addEventListener('pointermove', function (e) {
      if (!down || e.pointerId !== pointerId) return;
      var dx = e.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) <= THRESHOLD) return;
        moved = true;
        el.classList.add('is-dragging');
        el.style.scrollSnapType = 'none';
        el.style.scrollBehavior = 'auto';
        try { el.setPointerCapture(e.pointerId); } catch (err) {}
      }
      el.scrollLeft = startLeft - dx;
      e.preventDefault();
    });

    function release() {
      if (!down) return;
      down = false;
      pointerId = null;
      el.classList.remove('is-dragging');
      if (moved) {
        el.style.scrollSnapType = prevSnap;
        el.style.scrollBehavior = prevBehavior;
        el.addEventListener('click', function (ev) {
          ev.preventDefault();
          ev.stopPropagation();
        }, { capture: true, once: true });
        el.dispatchEvent(new Event('scroll'));
      }
      moved = false;
    }

    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', release);
    el.addEventListener('lostpointercapture', release);
  }

  function scan() {
    var nodes = document.querySelectorAll('ul, ol, div, nav');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.dataset.dragScroll === 'on') continue;
      if (el.scrollWidth - el.clientWidth <= 4) continue;
      if (skip(el)) continue;
      if (!scrollableX(el)) continue;
      bind(el);
    }
    var rows = document.querySelectorAll(ALL_ROWS);
    for (var j = 0; j < rows.length; j++) disableNativeDrag(rows[j]);
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    window.setTimeout(function () { queued = false; scan(); }, 150);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  window.addEventListener('load', schedule);
  window.addEventListener('resize', schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:reorder', schedule);

  if (window.MutationObserver) {
    new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
